import { getTimeRangeMinutes, getWeekdayCompactLabel } from '@quadralize/utils'
import { DAYS_PER_WEEK, FULL_PCT, MINS_PER_HOUR, MONTH_PT, SATURDAY_INDEX } from './constants'
import type {
  BlockedSlot,
  BusinessHours,
  PositionedBlockedSlot,
  PositionedEvent,
  SchedulerEvent,
  SchedulerSlotDuration,
} from './types'

// ─── Time Helpers ─────────────────────────────────────────────────────────────

const END_OF_DAY_HOUR = 23
const END_OF_DAY_MINUTE = 59
const END_OF_DAY_SECOND = 59
const END_OF_DAY_MILLISECOND = 999

/** Converts "HH:mm" to minutes since midnight */
export const toMin = (t: string): number => {
  const [h = 0, m = 0] = t.split(':').map(Number)
  return h * MINS_PER_HOUR + m
}

/**
 * End of an event in minutes since midnight. An end at or before the start
 * (e.g. 23:00 -> 00:00) wraps to the end of the day (1440).
 */
export const toEndMin = (start: string, end: string): number => getTimeRangeMinutes(start, end).end

export const nowMin = (): number => {
  const n = new Date()
  return n.getHours() * MINS_PER_HOUR + n.getMinutes()
}

export const nowLabel = (): string => {
  const n = new Date()
  return `${n.getHours().toString().padStart(2, '0')}:${n.getMinutes().toString().padStart(2, '0')}`
}

/** Formats an integer hour as a padded string + ":00" */
export const hourToLabel = (h: number): string => `${h.toString().padStart(2, '0')}:00`

export const toIso = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const formatDate = (d: Date): string =>
  `${getWeekdayCompactLabel(d.getDay())}, ${d.getDate()} de ${MONTH_PT[d.getMonth()]}`

export const startOfDay = (date: Date): Date => {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

export const endOfDay = (date: Date): Date => {
  const result = new Date(date)
  result.setHours(END_OF_DAY_HOUR, END_OF_DAY_MINUTE, END_OF_DAY_SECOND, END_OF_DAY_MILLISECOND)
  return result
}

export const formatDuration = (start: string, end: string): string => {
  const diff = toEndMin(start, end) - toMin(start)
  const h = Math.floor(diff / MINS_PER_HOUR)
  const m = diff % MINS_PER_HOUR
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

/** Returns a "HH:mm" string from hour + optional minutes */
export const hourToTimeString = (h: number, m = 0): string =>
  `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`

/** Returns Sunday → Saturday for the week containing `ref` */
export const getWeekDays = (ref: Date): Date[] => {
  const start = new Date(ref)
  const day = start.getDay()
  start.setDate(start.getDate() - day)
  return Array.from({ length: DAYS_PER_WEEK }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
}

export const getWeekRange = (ref: Date): { startDate: Date; endDate: Date } => {
  const days = getWeekDays(ref)
  const firstDay = days[0] ?? startOfDay(ref)
  const lastDay = days[DAYS_PER_WEEK - 1] ?? firstDay

  return {
    startDate: startOfDay(firstDay),
    endDate: endOfDay(lastDay),
  }
}

export const getMonthRange = (ref: Date): { startDate: Date; endDate: Date } => {
  const monthStart = new Date(ref.getFullYear(), ref.getMonth(), 1)
  const monthEnd = new Date(ref.getFullYear(), ref.getMonth() + 1, 0)

  return {
    startDate: startOfDay(monthStart),
    endDate: endOfDay(monthEnd),
  }
}

export const buildMonthDays = (ref: Date): Date[] => {
  const monthStart = new Date(ref.getFullYear(), ref.getMonth(), 1)
  const monthEnd = new Date(ref.getFullYear(), ref.getMonth() + 1, 0)

  const monthStartWeekDay = monthStart.getDay()
  const daysSinceSunday = monthStartWeekDay
  const gridStart = new Date(monthStart)
  gridStart.setDate(monthStart.getDate() - daysSinceSunday)

  const monthEndWeekDay = monthEnd.getDay()
  const daysUntilSaturday = SATURDAY_INDEX - monthEndWeekDay
  const gridEnd = new Date(monthEnd)
  gridEnd.setDate(monthEnd.getDate() + daysUntilSaturday)

  const days: Date[] = []
  const cursor = new Date(gridStart)
  while (cursor <= gridEnd) {
    days.push(new Date(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }

  return days
}

// ─── Collision Detection ──────────────────────────────────────────────────────

/** Greedy algorithm: places events in the first non-overlapping column */
export const positionEvents = (evts: SchedulerEvent[]): PositionedEvent[] => {
  if (!evts.length) return []

  const sorted = [...evts].sort((a, b) => toMin(a.startTime) - toMin(b.startTime))
  const columns: SchedulerEvent[][] = []

  for (const ev of sorted) {
    let placed = false
    for (const col of columns) {
      const last = col[col.length - 1]
      if (last && toEndMin(last.startTime, last.endTime) <= toMin(ev.startTime)) {
        col.push(ev)
        placed = true
        break
      }
    }
    if (!placed) columns.push([ev])
  }

  const result: PositionedEvent[] = []
  for (let ci = 0; ci < columns.length; ci++) {
    const col = columns[ci]
    if (!col) continue
    for (const ev of col) {
      const s = toMin(ev.startTime)
      const e = toEndMin(ev.startTime, ev.endTime)
      const overlap = columns.filter((c) =>
        c.some((x) => toMin(x.startTime) < e && toEndMin(x.startTime, x.endTime) > s),
      ).length
      result.push({ ...ev, column: ci, totalColumns: overlap })
    }
  }
  return result
}

/** Same greedy algorithm applied to blocked slots */
export const positionBlockedSlots = (slots: BlockedSlot[]): PositionedBlockedSlot[] => {
  if (!slots.length) return []

  const sorted = [...slots].sort((a, b) => toMin(a.startTime) - toMin(b.startTime))
  const columns: BlockedSlot[][] = []

  for (const slot of sorted) {
    let placed = false
    for (const col of columns) {
      const last = col[col.length - 1]
      if (last && toMin(last.endTime) <= toMin(slot.startTime)) {
        col.push(slot)
        placed = true
        break
      }
    }
    if (!placed) columns.push([slot])
  }

  const result: PositionedBlockedSlot[] = []
  for (let ci = 0; ci < columns.length; ci++) {
    const col = columns[ci]
    if (!col) continue
    for (const slot of col) {
      const s = toMin(slot.startTime)
      const e = toMin(slot.endTime)
      const overlap = columns.filter((c) =>
        c.some((x) => toMin(x.startTime) < e && toMin(x.endTime) > s),
      ).length
      result.push({ ...slot, column: ci, totalColumns: overlap })
    }
  }
  return result
}

// ─── Occupancy ────────────────────────────────────────────────────────────────

export const calcOccupancy = (evts: SchedulerEvent[], startH: number, endH: number): number => {
  const total = (endH - startH) * MINS_PER_HOUR
  if (!total) return 0

  const intervals = evts
    .map((e) => ({
      s: Math.max(toMin(e.startTime), startH * MINS_PER_HOUR),
      e: Math.min(toEndMin(e.startTime, e.endTime), endH * MINS_PER_HOUR),
    }))
    .filter((i) => i.e > i.s)
    .sort((a, b) => a.s - b.s)

  let booked = 0
  let lastEnd = 0
  for (const { s, e } of intervals) {
    const eff = Math.max(s, lastEnd)
    if (e > eff) {
      booked += e - eff
      lastEnd = e
    }
  }
  return Math.round((booked / total) * FULL_PCT)
}

// ─── Mobile Slot Builder ──────────────────────────────────────────────────────

/** Represents one row in the mobile agenda view */
export interface TimeSlot {
  timeStr: string
  minutes: number
  events: SchedulerEvent[]
  blocked: BlockedSlot | null
  isBlockedStart: boolean
  blockedDuration: number
  isOffHours: boolean
  isOffHoursStart: boolean
  offHoursDuration: number
}

/** Builds the list of time slots for the mobile agenda */
export const buildSlots = (
  startTime: number,
  endTime: number,
  slotDuration: SchedulerSlotDuration,
  events: SchedulerEvent[],
  blocked: BlockedSlot[],
  businessHours: BusinessHours | undefined,
): TimeSlot[] => {
  const slots: TimeSlot[] = []
  const totalMins = (endTime - startTime) * MINS_PER_HOUR

  for (let offset = 0; offset < totalMins; offset += slotDuration) {
    const minutes = startTime * MINS_PER_HOUR + offset
    const h = Math.floor(minutes / MINS_PER_HOUR)
    const m = minutes % MINS_PER_HOUR
    const timeStr = hourToTimeString(h, m)
    const slotEnd = Math.min(minutes + slotDuration, endTime * MINS_PER_HOUR)

    const slotEvents = events.filter(
      (event) =>
        toMin(event.startTime) < slotEnd && toEndMin(event.startTime, event.endTime) > minutes,
    )

    const blockedMatch =
      blocked.find((slot) => toMin(slot.startTime) < slotEnd && toMin(slot.endTime) > minutes) ??
      null

    const isBlockedStart =
      blockedMatch !== null &&
      toMin(blockedMatch.startTime) >= minutes &&
      toMin(blockedMatch.startTime) < slotEnd

    const blockedDuration = blockedMatch
      ? toMin(blockedMatch.endTime) - toMin(blockedMatch.startTime)
      : 0

    const isOffHours = businessHours
      ? minutes < businessHours.start * MINS_PER_HOUR || slotEnd > businessHours.end * MINS_PER_HOUR
      : false

    const isOffHoursStart =
      isOffHours &&
      (offset === 0 ||
        !!(
          businessHours &&
          minutes >= businessHours.end * MINS_PER_HOUR &&
          minutes < businessHours.end * MINS_PER_HOUR + slotDuration
        ))

    let offHoursDuration = 0
    if (isOffHoursStart) {
      if (offset === 0 && businessHours) {
        offHoursDuration = Math.max(
          0,
          businessHours.start * MINS_PER_HOUR - startTime * MINS_PER_HOUR,
        )
      } else if (businessHours) {
        const offHoursStart = Math.max(minutes, businessHours.end * MINS_PER_HOUR)
        offHoursDuration = Math.max(0, endTime * MINS_PER_HOUR - offHoursStart)
      }
    }

    slots.push({
      timeStr,
      minutes,
      events: slotEvents,
      blocked: blockedMatch,
      isBlockedStart,
      blockedDuration,
      isOffHours,
      isOffHoursStart,
      offHoursDuration,
    })
  }
  return slots
}
